import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/firebase';

// Users Collection
const USERS_COLLECTION = 'users';

/**
 * Get user profile from Firestore
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} User profile document or null if not found
 */
export const getUserProfile = async (userId) => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return {
        id: userSnap.id,
        ...userSnap.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

/**
 * Create or update user profile in Firestore
 * @param {string} userId - User ID
 * @param {Object} userData - User data to save
 * @param {string} userData.email - User email
 * @param {string} [userData.firstName] - User first name
 * @param {string} [userData.lastName] - User last name
 * @param {string} [userData.avatarPhoto] - User avatar photo URL
 * @param {string} [userData.bio] - User bio
 * @param {string} [userData.language] - User preferred language ('en' or 'es')
 * @returns {Promise<Object>} Created/updated user profile
 */
export const createOrUpdateUserProfile = async (userId, userData) => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    const userSnap = await getDoc(userRef);
    
    const profileData = {
      userId,
      email: userData.email,
      updatedAt: serverTimestamp(),
      ...(userData.firstName && userData.firstName.trim() && { firstName: userData.firstName.trim() }),
      ...(userData.lastName && userData.lastName.trim() && { lastName: userData.lastName.trim() }),
      ...(userData.avatarPhoto && userData.avatarPhoto.trim() && { avatarPhoto: userData.avatarPhoto.trim() }),
      ...(userData.bio && userData.bio.trim() && { bio: userData.bio.trim() }),
      ...(userData.language && (userData.language === 'en' || userData.language === 'es') && { language: userData.language })
    };

    if (userSnap.exists()) {
      // Update existing profile - no need to read again, return what we know
      await updateDoc(userRef, profileData);
      return {
        id: userSnap.id,
        ...userSnap.data(),
        ...profileData
      };
    } else {
      // Create new profile - no need to read again, return what we know
      const newProfileData = {
        ...profileData,
        createdAt: serverTimestamp()
      };
      await setDoc(userRef, newProfileData);
      return {
        id: userId,
        ...newProfileData
      };
    }
  } catch (error) {
    console.error('Error creating/updating user profile:', error);
    throw error;
  }
};

/**
 * Update user language preference
 * Only writes if language actually changed to avoid unnecessary writes
 * @param {string} userId - User ID
 * @param {string} language - Language code ('en' or 'es')
 * @returns {Promise<void>}
 */
export const updateUserLanguage = async (userId, language) => {
  try {
    if (language !== 'en' && language !== 'es') {
      throw new Error('Invalid language code. Must be "en" or "es"');
    }

    // Check current language to avoid unnecessary writes
    const userRef = doc(db, USERS_COLLECTION, userId);
    const userSnap = await getDoc(userRef);
    
    // Only update if language is different or doesn't exist
    if (!userSnap.exists() || userSnap.data().language !== language) {
      await updateDoc(userRef, {
        language,
        updatedAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error updating user language:', error);
    throw error;
  }
};

/**
 * Initialize user profile from Firebase Auth user object
 * Extracts available information from auth user and creates/updates profile
 * @param {Object} authUser - Firebase Auth user object
 * @returns {Promise<Object>} User profile
 */
export const initializeUserProfile = async (authUser) => {
  if (!authUser) {
    throw new Error('Auth user is required');
  }

  // Extract name from displayName if available
  let firstName = '';
  let lastName = '';
  if (authUser.displayName) {
    const nameParts = authUser.displayName.trim().split(/\s+/);
    firstName = nameParts[0] || '';
    lastName = nameParts.slice(1).join(' ') || '';
  }

  // Extract email
  const email = authUser.email || '';

  // Extract photo URL
  const avatarPhoto = authUser.photoURL || null;

  // Check if profile already exists
  const userRef = doc(db, USERS_COLLECTION, authUser.uid);
  const userSnap = await getDoc(userRef);

  // If profile exists, check if we need to update anything
  if (userSnap.exists()) {
    const existingProfile = userSnap.data();
    
    // Only update fields that are missing or have new values
    const updateData = {};
    if (!existingProfile.firstName && firstName) updateData.firstName = firstName;
    if (!existingProfile.lastName && lastName) updateData.lastName = lastName;
    if (!existingProfile.avatarPhoto && avatarPhoto) updateData.avatarPhoto = avatarPhoto;
    if (!existingProfile.email && email) updateData.email = email;

    // Only write if there are actual changes
    if (Object.keys(updateData).length > 0) {
      await updateDoc(userRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
      return {
        id: authUser.uid,
        ...existingProfile,
        ...updateData
      };
    }
    
    // No changes needed, return existing profile
    return {
      id: authUser.uid,
      ...existingProfile
    };
  } else {
    // Create new profile - only include fields that have values
    const newProfileData = {
      userId: authUser.uid,
      email,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      ...(firstName && { firstName }),
      ...(lastName && { lastName }),
      ...(avatarPhoto && { avatarPhoto })
    };
    
    await setDoc(userRef, newProfileData);
    return {
      id: authUser.uid,
      ...newProfileData
    };
  }
};

